/**
 * FastSpotlightSection — homepage "Verified On-Time Delivery
 * Restaurants" spotlight. Surfaces Fast by NoFee restaurants in
 * priority order by internal Fast Score (higher first).
 *
 * iter134 (Jul 2026) — Owner spec: retire the public tier ladder
 * (Standard / Plus / Premium / Thunder). Restaurants are still
 * sorted internally by Fast Score so top performers appear first,
 * but the section renders a single "⚡ Fast by NoFee" badge on
 * each card — no tier labels, no public ranking language.
 *
 * Pulls from `GET /api/public/restaurants?fast_only=true` (already
 * wired in server.py) and sorts client-side by fast_score. Keeps
 * the top 6 entries.
 *
 * The section is hidden gracefully when there are no Fast-enabled
 * restaurants yet, so empty-state never appears.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, ArrowRight } from 'lucide-react';
import FastByNoFeeBadge from './FastByNoFeeBadge';
import FoodImage from './FoodImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FastSpotlightSection = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/restaurants`, { params: { fast_only: true } });
        if (cancelled) return;
        setRestaurants(res.data?.restaurants || []);
      } catch (_) {
        if (!cancelled) setRestaurants([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const ranked = useMemo(() => {
    const withScore = restaurants.map((r) => ({
      ...r,
      _score: Number(r?.fast_stats?.fast_score || 0),
    }));
    withScore.sort((a, b) => b._score - a._score);
    return withScore.slice(0, 6);
  }, [restaurants]);

  // Hide gracefully when no Fast-enabled restaurants exist yet.
  if (loading || ranked.length === 0) return null;

  return (
    <section
      className="relative bg-zinc-950 text-white py-16 sm:py-20 border-y border-zinc-900/80"
      data-testid="fast-spotlight-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Heading */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold mb-2">
              Fast Spotlight
            </p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">
              Verified On-Time Delivery Restaurants
            </h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-xl">
              These restaurants consistently deliver orders within their promised time — confirmed by real customers.
            </p>
          </div>
          <Link
            to="/restaurants?fast_only=true"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-yellow-400/30 text-yellow-200 hover:bg-yellow-400/10 transition-colors text-[12.5px] font-bold"
            data-testid="fast-spotlight-see-all"
          >
            See all Fast restaurants <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
          data-testid="fast-spotlight-grid"
        >
          {ranked.map((r) => {
            return (
              <article
                key={r.id}
                data-testid={`fast-spotlight-card-${r.slug}`}
                className="group relative overflow-hidden rounded-2xl bg-zinc-900/70 border border-white/[0.06] hover:border-yellow-400/40 hover:bg-zinc-900 transition-all"
              >
                <Link to={`/menu/${r.slug}`} className="flex items-stretch gap-4 p-4">
                  {/* Logo */}
                  <FoodImage
                    src={r.logo_url}
                    alt={r.name}
                    variant="thumb"
                    aspectClass="w-20 h-20 sm:w-24 sm:h-24 shrink-0"
                    rounded="rounded-xl"
                  />
                  <div className="flex-1 min-w-0 flex flex-col">
                    <h3 className="text-[15px] sm:text-base font-bold text-white truncate group-hover:text-yellow-200 transition-colors">
                      {r.name}
                    </h3>
                    {r.cuisine_type && (
                      <p className="text-[12px] text-zinc-400 capitalize truncate">{r.cuisine_type}</p>
                    )}
                    <div className="mt-2">
                      <FastByNoFeeBadge
                        variant="card"
                        fastEnabled
                        restaurantName={r.name}
                      />
                    </div>
                    {(() => {
                      // Dynamic ETA — legacy `estimated_time` is intentionally
                      // ignored to avoid stale 30-45 min defaults from older
                      // docs surfacing on customer-facing cards.
                      const pickup = (r.pickup_enabled ?? r.accepts_pickup) && r.pickup_avg_minutes;
                      const delivery = (r.delivery_enabled ?? r.accepts_delivery) && r.delivery_avg_minutes;
                      if (!pickup && !delivery) return null;
                      const parts = [];
                      if (pickup) parts.push(`Pickup ${r.pickup_avg_minutes}m`);
                      if (delivery) parts.push(`Delivery ${r.delivery_avg_minutes}m`);
                      return (
                        <p className="mt-auto pt-2 inline-flex items-center gap-1 text-[11.5px] text-zinc-400">
                          <Clock className="w-3 h-3" /> {parts.join(' · ')}
                        </p>
                      );
                    })()}
                  </div>
                </Link>
              </article>
            );
          })}
        </div>

        {/* Mobile "See all" CTA */}
        <div className="mt-6 sm:hidden">
          <Link
            to="/restaurants?fast_only=true"
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-yellow-400/30 text-yellow-200 text-[12.5px] font-bold"
            data-testid="fast-spotlight-see-all-mobile"
          >
            See all Fast restaurants <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FastSpotlightSection;
